"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useLayoutEffect } from "react";
import { TerraLink } from "../../component/TerraLink/TerraLink";
import { Icon } from "@/components/formatters/MXPIcon";
import { CommonDescription } from "../../component/CommonDescription/CommonDescription";
import { darkTextureWrapper, colorSettingDetection, renderHeaderConditionally } from "../../utils/functions";
import { layoutHeaderClasses, layoutHeaderBigClasses, layoutSubheaderClasses, layoutEyebrowClasses } from "@/components/tailwindHelpers";
import { AccordionGroup } from "../../component/Accordion/AccordionGroup";
import { useAccordionGroup } from "../../component/Accordion/AccordionGroupContext";
import { useEcomStore } from "@/context/ecom/EcomContext";
export interface TrueFaqQuestion {
  question: string;
  answer: string;
}
export interface TrueFaqTabGroup {
  title: string;
  items: TrueFaqQuestion[];
}
export type TrueFaqEntry = {
  type: "faq";
  data: TrueFaqQuestion;
} | {
  type: "tab";
  data: TrueFaqTabGroup;
};
export interface LayoutTrueFAQProps {
  eyebrow?: string;
  header?: string;
  headerElement?: "h1" | "h2";
  subheader?: string;
  description?: string;
  presetTheme?: "t2-theme-one" | "t2-theme-two" | "t2-theme-three" | "t2-theme-four" | "t2-theme-five" | "t2-theme-six" | "t2-theme-seven";
  items: TrueFaqEntry[];
  buttons?: TerraLink[];
  isHeaderBig?: boolean;
  isEmbedded?: boolean;
}
function slugify(text: string): string {
  if (!text) return "";
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
const MAX_TABS = 8;
const ACCORDION_ITEM = ["border-b border-gray-m2 py-8 px-5 md:py-10 md:px-16 lg:px-20 xl:px-28"].join(" ");
const ACCORDION_OPEN = "bg-gray-m1 border-dashed !border-gray-m3 !border-t t2-dark:bg-gray-m10 t2-dark:!border-gray-m7";
const HEADLINE_CLASSES = ["text-xl font-bold leading-relaxed max-w-[747px]", "max-lg:leading-snug max-lg:font-semibold max-lg:max-w-[560px] max-lg:m-0", "max-md:text-lg max-md:max-w-[400px]", "max-sm:text-base max-sm:max-w-[280px]", "t2-dark:text-white"].join(" ");
const TOGGLE_BTN = ["flex justify-center items-center w-[50px] h-[50px] text-gray-m9 border border-gray-m9 rounded-full bg-white text-2xl leading-none cursor-pointer shrink-0", "[&_svg]:p-1", "max-sm:w-10 max-sm:h-10 max-sm:text-xl"].join(" ");
const ANSWER_BASE = ["text-2xl font-normal leading-9 max-h-0 overflow-hidden transition-[max-height,padding] duration-300 ease-in-out", "max-lg:text-xl max-lg:leading-8", "max-md:text-lg max-md:leading-7", "max-sm:text-base max-sm:leading-snug max-sm:font-light", "[&_.faq-answer-content]:max-w-[747px] [&_.faq-answer-content]:mb-[26px]"].join(" ");
const TAB_BUTTON_BASE = ["px-6 py-3 text-sm font-bold uppercase tracking-wide cursor-pointer transition-colors duration-200", "border-b-[3px] border-solid", "max-sm:px-4 max-sm:py-2 max-sm:text-xs"].join(" ");
const TAB_ACTIVE = "border-b-blue-dnavy text-blue-dnavy t2-dark:border-b-yellow-m10 t2-dark:text-yellow-m10";
const TAB_INACTIVE = "border-b-transparent text-gray-m6 hover:text-gray-m9 hover:border-b-gray-m3 t2-dark:text-gray-m3 t2-dark:hover:text-white t2-dark:hover:border-b-gray-m5";
function FaqAccordionItem(t0) {
  const $ = _c(41);
  const {
    question,
    answer,
    itemKey
  } = t0;
  const group = useAccordionGroup();
  const registerItem = group?.registerItem;
  let t1;
  if ($[0] !== question) {
    t1 = slugify(question);
    $[0] = question;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const anchorId = `faq-${t1}`;
  let t2;
  let t3;
  if ($[2] !== itemKey || $[3] !== registerItem) {
    t2 = () => {
      if (registerItem) {
        return registerItem(itemKey);
      }
    };
    t3 = [registerItem, itemKey];
    $[2] = itemKey;
    $[3] = registerItem;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useLayoutEffect(t2, t3);
  let t4;
  if ($[6] !== group || $[7] !== itemKey) {
    t4 = group?.isItemOpen(itemKey) ?? false;
    $[6] = group;
    $[7] = itemKey;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isOpen = t4;
  let t5;
  if ($[9] !== group || $[10] !== itemKey) {
    t5 = () => group?.toggleItem(itemKey);
    $[9] = group;
    $[10] = itemKey;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const onToggle = t5;
  const t6 = `${ACCORDION_ITEM} ${isOpen ? ACCORDION_OPEN : ""}`;
  const t7 = `${anchorId}-answer`;
  let t8;
  if ($[12] !== onToggle) {
    t8 = e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle();
      }
    };
    $[12] = onToggle;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== question) {
    t9 = <h3 className={HEADLINE_CLASSES}>{question}</h3>;
    $[14] = question;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = `${TOGGLE_BTN} ${isOpen ? "bg-yellow-m10 border-0" : ""}`;
  const t11 = isOpen ? `Collapse ${question}` : `Expand ${question}`;
  const t12 = isOpen ? "t2u-collapse-open" : "t2u-collapse-close";
  let t13;
  if ($[16] !== t12) {
    t13 = <Icon type={t12} />;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t13) {
    t14 = <span className={t10} aria-label={t11}>{t13}</span>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== isOpen || $[23] !== onToggle || $[24] !== t14 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t15 = <div className="cursor-pointer flex justify-between items-center max-sm:items-start max-sm:mb-3" onClick={onToggle} aria-expanded={isOpen} aria-controls={t7} role="button" tabIndex={0} onKeyDown={t8}>{t9}{t14}</div>;
    $[22] = isOpen;
    $[23] = onToggle;
    $[24] = t14;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = `${ANSWER_BASE} ${isOpen ? "max-h-[2000px]" : ""}`;
  const t17 = `${anchorId}-answer`;
  let t18;
  if ($[29] !== answer) {
    t18 = <CommonDescription description={answer} className="faq-answer-content t2-layout__description" />;
    $[29] = answer;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== anchorId || $[32] !== t16 || $[33] !== t17 || $[34] !== t18) {
    t19 = <div className={t16} id={t17} role="region" aria-labelledby={anchorId}>{t18}</div>;
    $[31] = anchorId;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== anchorId || $[37] !== t15 || $[38] !== t19 || $[39] !== t6) {
    t20 = <div className={t6} id={anchorId}>{t15}{t19}</div>;
    $[36] = anchorId;
    $[37] = t15;
    $[38] = t19;
    $[39] = t6;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  return t20;
}
function FaqList(t0) {
  const $ = _c(7);
  const {
    faqs,
    keyPrefix
  } = t0;
  let t1;
  if ($[0] !== faqs || $[1] !== keyPrefix) {
    let t2;
    if ($[3] !== keyPrefix) {
      t2 = (faq, i) => {
        const key = `${keyPrefix}${slugify(faq.question)}-${i}`;
        return <FaqAccordionItem key={key} question={faq.question} answer={faq.answer} itemKey={key} />;
      };
      $[3] = keyPrefix;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = faqs.map(t2);
    $[0] = faqs;
    $[1] = keyPrefix;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export const LayoutTrueFAQ = t0 => {
  const $ = _c(58);
  const {
    eyebrow,
    header,
    headerElement: t1,
    subheader,
    description,
    presetTheme: t2,
    items,
    buttons,
    isHeaderBig,
    isEmbedded
  } = t0;
  const headerElement = t1 === undefined ? "h2" : t1;
  const presetTheme = t2 === undefined ? "t2-theme-one" : t2;
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [state] = useEcomStore() ?? [];
  const expandAllLabel = state?.globalEcommerceLabels?.expandAllLabel ?? "Expand All";
  let t3;
  if ($[0] !== isEmbedded || $[1] !== presetTheme) {
    t3 = isEmbedded ? "" : colorSettingDetection(presetTheme || "");
    $[0] = isEmbedded;
    $[1] = presetTheme;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const colorSetting = t3;
  const mainClasses = isEmbedded ? "t2-layout-true-faq" : `t2-layout-true-faq py-[100px] ${presetTheme || ""} ${colorSetting}`;
  const headerClasses = `t2-layout-true-faq__header ${isHeaderBig ? layoutHeaderBigClasses : layoutHeaderClasses}`;
  const t4 = header || "";
  let t5;
  if ($[3] !== headerClasses || $[4] !== headerElement || $[5] !== t4) {
    t5 = renderHeaderConditionally(headerClasses, t4, headerElement);
    $[3] = headerClasses;
    $[4] = headerElement;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const finalHeader = t5;
  let t6;
  if ($[7] !== isEmbedded || $[8] !== presetTheme) {
    t6 = isEmbedded ? undefined : darkTextureWrapper(presetTheme || "");
    $[7] = isEmbedded;
    $[8] = presetTheme;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const componentStyles = t6;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t7;
  let t8;
  let t9;
  if ($[10] !== activeTabIndex || $[11] !== componentStyles || $[12] !== description || $[13] !== expandAllLabel || $[14] !== eyebrow || $[15] !== finalHeader || $[16] !== isEmbedded || $[17] !== items || $[18] !== mainClasses || $[19] !== subheader) {
    const standaloneFaqs = [];
    const tabGroups = [];
    let standaloneIdx = 0;
    for (const entry of items) {
      if (entry.type === "faq") {
        standaloneFaqs.push({
          faq: entry.data,
          globalIdx: standaloneIdx
        });
        standaloneIdx++;
      } else {
        tabGroups.push(entry.data);
      }
    }
    const visibleTabs = tabGroups.slice(0, MAX_TABS);
    t14 = mainClasses;
    t15 = componentStyles;
    t16 = "layout-true-faq";
    t13 = isEmbedded ? undefined : "w-full px-5 lg:px-20 m-auto max-w-[1440px]";
    t11 = "t2-layout__content";
    if ($[31] !== description || $[32] !== eyebrow || $[33] !== finalHeader || $[34] !== isEmbedded || $[35] !== subheader) {
      t12 = !isEmbedded && <div className="pb-12"><div className="t2-header-area">{eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}{finalHeader}{subheader && <div className={layoutSubheaderClasses}>{subheader}</div>}{description && <CommonDescription description={description} className="t2-layout__description" />}</div></div>;
      $[31] = description;
      $[32] = eyebrow;
      $[33] = finalHeader;
      $[34] = isEmbedded;
      $[35] = subheader;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    T0 = AccordionGroup;
    t7 = true;
    t8 = expandAllLabel;
    t9 = standaloneFaqs.length > 0 && <div data-testid="faq-standalone-list">{standaloneFaqs.map(_temp)}</div>;
    t10 = visibleTabs.length > 0 && <div data-testid="faq-tabs" className={standaloneFaqs.length > 0 ? "mt-12" : ""}><div className="flex border-b border-gray-m2 mb-0 overflow-x-auto" role="tablist">{visibleTabs.map((tab, i) => <button key={`tab-btn-${i}`} role="tab" aria-selected={i === activeTabIndex} aria-controls={`faq-tabpanel-${i}`} className={`${TAB_BUTTON_BASE} ${i === activeTabIndex ? TAB_ACTIVE : TAB_INACTIVE}`} onClick={() => setActiveTabIndex(i)}>{tab.title}</button>)}</div>{visibleTabs.map((tab_0, tabIdx) => {
        const isActive = tabIdx === activeTabIndex;
        return <div key={`tabpanel-${tabIdx}`} id={`faq-tabpanel-${tabIdx}`} role="tabpanel" className={isActive ? "block" : "hidden"} aria-hidden={!isActive}><FaqList faqs={tab_0.items} keyPrefix={`tab-${slugify(tab_0.title)}-`} /></div>;
      })}</div>;
    $[10] = activeTabIndex;
    $[11] = componentStyles;
    $[12] = description;
    $[13] = expandAllLabel;
    $[14] = eyebrow;
    $[15] = finalHeader;
    $[16] = isEmbedded;
    $[17] = items;
    $[18] = mainClasses;
    $[19] = subheader;
    $[20] = T0;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    T0 = $[20];
    t10 = $[21];
    t11 = $[22];
    t12 = $[23];
    t13 = $[24];
    t14 = $[25];
    t15 = $[26];
    t16 = $[27];
    t7 = $[28];
    t8 = $[29];
    t9 = $[30];
  }
  let t17;
  if ($[37] !== T0 || $[38] !== t10 || $[39] !== t7 || $[40] !== t8 || $[41] !== t9) {
    t17 = <T0 showExpandAll={t7} expandAllLabel={t8}>{t9}{t10}</T0>;
    $[37] = T0;
    $[38] = t10;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== buttons) {
    t18 = buttons && buttons.length > 0 && <div className="flex gap-x-4 flex-col sm:flex-row justify-center pt-[50px]">{buttons.map(_temp2)}</div>;
    $[43] = buttons;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t11 || $[46] !== t12 || $[47] !== t17 || $[48] !== t18) {
    t19 = <div className={t11}>{t12}{t17}{t18}</div>;
    $[45] = t11;
    $[46] = t12;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t13 || $[51] !== t19) {
    t20 = <div className={t13}>{t19}</div>;
    $[50] = t13;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== t14 || $[54] !== t15 || $[55] !== t16 || $[56] !== t20) {
    t21 = <div className={t14} style={t15} data-testid={t16}>{t20}</div>;
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t20;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  return t21;
};
function _temp(t0) {
  const {
    faq,
    globalIdx
  } = t0;
  const key = `${slugify(faq.question)}-${globalIdx}`;
  return <FaqAccordionItem key={key} question={faq.question} answer={faq.answer} itemKey={key} />;
}
function _temp2(btn, index) {
  return <TerraLink key={`button-${index}`} {...btn} />;
}